<template>
  <error-boundary error-key="restrictionsServiceGroups">
    <div class="d-flex justify-space-between">
      <create-restriction-button
        :operation="serviceGroupsRestrictions?.operations.add"
        @click="setIsRestrictionDialogOpen(true)"
      />
      <v-spacer />
      <restrictions-table-toggle-button
        :active="isRestrictionsTableOpen"
        @click="setIsRestrictionsTableOpen(!isRestrictionsTableOpen)"
      />
    </div>

    <div class="d-lg-flex flex-lg-row-reverse mt-md-6">
      <restrictions-table
        v-model:open="isRestrictionsTableOpen"
        :period="serviceGroupsRestrictionsChessPeriod"
        :table-headers="restrictionsTableHeaders"
        :table-items="filteredRestrictionsTableItems"
        :operation-edit="serviceGroupsRestrictions?.operations.edit"
        :operation-delete="serviceGroupsRestrictions?.operations.remove"
        @show="handleRestrictionShow"
        @edit="handleRestrictionEdit"
        @remove="handleRestrictionRemove"
        @open:chess="handleChessOpen"
      >
        <template v-slot:[`item.number`]="{ item }"> #{{ item.restriction.number }}</template>
        <template v-slot:[`item.serviceGroup`]="{ item }"> {{ item.restrictionFactor.name }}</template>
        <template v-slot:[`item.period`]="{ item }">
          {{ toShortDateFormat(item.period.from) }} <br />
          {{ toShortDateFormat(item.period.to) }}
        </template>
        <template v-slot:[`item.options`]="{ item }">
          <div>{{ item.restriction.options?.isOpened ? 'Открыта' : 'Закрыта' }}</div>
          <template v-if="item.restriction.options?.isOpened">
            <div>{{ item.restriction.options?.options?.isNeedConfirm ? 'Запрос' : 'Гарантия' }}</div>
            <div>{{ item.restriction.options?.options?.orderPeriod ? 'Разрешен' : 'Запрещен' }}</div>
            <div>{{ item.restriction.options?.options?.orderPeriod?.minOrderPeriod || 'Не ограничен' }}</div>
            <div>{{ item.restriction.options?.options?.orderPeriod?.maxOrderPeriod || 'Не ограничен' }}</div>
          </template>
          <template v-else>
            <div>-</div>
            <div>-</div>
            <div>-</div>
          </template>
        </template>
        <template #filters>
          <e-select
            v-model="filters.serviceGroup"
            :items="serviceGroupItems"
            item-title="name"
            item-value="cid"
            label="Группа услуг"
            clearable
          />
          <e-select
            v-model="filters.confirmationType"
            :items="confirmationTypeItems"
            label="Тип подтверждения"
            clearable
          />
        </template>
      </restrictions-table>
      <chess
        v-if="$vuetify.display.mdAndUp"
        ref="chessRef"
        :period="serviceGroupsRestrictionsChessPeriod"
        :items="chessItems"
        :chess-dates-available-from="chessDatesAvailableFrom"
        :is-edit="
          isOperationEnabled(serviceGroupsRestrictions?.operations.add) ||
          isOperationEnabled(serviceGroupsRestrictions?.operations.edit)
        "
        :loading="isLoading"
        @select="handleChessSelect"
      />
    </div>

    <service-groups-restriction-dialog
      v-model="isRestrictionDialogOpen"
      :selected-cells="selectedChessCells"
      :is-edit-available="
        isOperationEnabled(serviceGroupsRestrictions?.operations.add) ||
        isOperationEnabled(serviceGroupsRestrictions?.operations.edit)
      "
      @close="chessRef?.unselectAllCells"
    />
    <remove-restriction-dialog
      v-model="isRemoveRestrictionDialogOpen"
      :operation-delete="serviceGroupsRestrictions?.operations.remove"
      :number="selectedChessCells[0]?.rangeId"
      :action="removeRestrictionAction"
    />

    <e-dialog v-model="isChessCalendarDialogOpen">
      <template #title>
        <div class="caption">Ограничения ГУ</div>
      </template>
      <e-select v-model="selectedRow" :items="rowList" label="Группа услуг" />
      <chess-calendar
        v-if="selectedTable && selectedRow"
        ref="chessCalendarRef"
        class="mt-6"
        :period="serviceGroupsRestrictionsChessPeriod"
        :table="selectedTable"
        :cells="selectedRow.cells"
        :is-edit="
          isOperationEnabled(serviceGroupsRestrictions?.operations.add) ||
          isOperationEnabled(serviceGroupsRestrictions?.operations.edit)
        "
        :chess-dates-available-from="chessDatesAvailableFrom"
        @select="handleChessSelect"
      />
    </e-dialog>
  </error-boundary>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { computed, nextTick, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import type {
  ChessCalendarComponent,
  ChessComponent,
  ChessDateCellType,
  ChessItemType,
  ChessTableRowCellsType,
  ChessTableRowType,
  ChessTableType,
} from '@/core/components/interface/Chess/Chess.type';
import type { ArrayElementType } from '@/core/types/gengeric';
import type { RangeOfDateOnly } from '@/graphql/generated';
import type { ServiceGroupsRestrictionsCellDataType } from '@/parts/tariffs/store/restrictions';
import ChessCalendar from '@/core/components/interface/Chess/ChessCalendar.vue';
import Chess from '@/core/components/interface/Chess/index.vue';
import EDialog from '@/core/components/modals/Dialog.vue';
import { ESelect } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import useToggle from '@/core/composables/useToggle';
import { getIsoDateWithUtcOffset, toShortDateFormat } from '@/core/utils/date';
import { isOperationEnabled } from '@/core/utils/permissions';
import ErrorBoundary from '@/parts/errors/components/ErrorBoundary.vue';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import CreateRestrictionButton from '@/parts/tariffs/components/PricesAndRestrictions/CreateRestrictionButton.vue';
import RemoveRestrictionDialog from '@/parts/tariffs/components/PricesAndRestrictions/RemoveRestrictionDialog.vue';
import RestrictionsTable from '@/parts/tariffs/components/PricesAndRestrictions/RestrictionsTable.vue';
import RestrictionsTableToggleButton from '@/parts/tariffs/components/PricesAndRestrictions/RestrictionsTableToggleButton.vue';
import ServiceGroupsRestrictionDialog from '@/parts/tariffs/components/PricesAndRestrictions/ServiceGroups/ServiceGroupsRestrictionDialog.vue';
import { useRestrictionsStore } from '@/parts/tariffs/store/restrictions';

const $route = useRoute();
const display = useDisplay();
const [isRestrictionsTableOpen, setIsRestrictionsTableOpen] = useToggle();
const [isRemoveRestrictionDialogOpen, setIsRemoveRestrictionDialogOpen] = useToggle();
const [isRestrictionDialogOpen, setIsRestrictionDialogOpen] = useToggle();
const [isChessCalendarDialogOpen, setIsChessCalendarDialogOpen] = useToggle();
const {
  serviceGroupsRestrictions,
  serviceGroupsRestrictionsChessPeriod,
  getServiceGroupsRestrictions,
  removeServiceGroupsRestriction,
} = useRestrictionsStore();
const { timeZoneOffsetMin } = useAccommodationObjectsStore();

const chessRef = ref<ChessComponent>();
const chessCalendarRef = ref<ChessCalendarComponent>();
const selectedChessCells = ref<ChessDateCellType<ServiceGroupsRestrictionsCellDataType>[]>([]);

const tariffScheduleCid = computed(() => String($route.params.t_id));

const chessItems = computed<ChessItemType<ServiceGroupsRestrictionsCellDataType>[]>(() => {
  if (!serviceGroupsRestrictions.value) return [];

  return [
    {
      tables: [
        {
          rows: serviceGroupsRestrictions.value.grid.map((gridItem) => {
            return {
              fixed: {
                title: gridItem.restrictionFactor.name ?? '',
                text:
                  '<div class="mt-md-1">Статус продажи</div>' +
                  '<div class="mt-md-05">Тип подтверждения</div>' +
                  '<div class="mt-md-05">Условия заезда</div>' +
                  '<div class="mt-md-05">Мин. заезд</div>' +
                  '<div class="mt-md-05">Макс. заезд</div>',
                verticalAlign: 'end',
              },
              cells: gridItem.restrictionPeriods.reduce<ChessTableRowCellsType<ServiceGroupsRestrictionsCellDataType>>(
                (result, restrictionPeriod) => {
                  return {
                    ...result,
                    ...getChessCellsFromGridItem(restrictionPeriod.period, {
                      serviceGroup: gridItem.restrictionFactor.cid,
                      restrictionId: restrictionPeriod.restrictionId,
                    }),
                  };
                },
                {},
              ),
            };
          }),
          fixedWidth: 160,
          rowCellWidth: 112,
          cellHeight: display.xs.value ? 98 : 128,
        },
      ],
    },
  ];
});

const chessDatesAvailableFrom = computed(() => {
  return getIsoDateWithUtcOffset(new Date(), timeZoneOffsetMin.value);
});

type ServiceGroupRestriction = {
  restriction: {
    number: number;
  };
  restrictionFactor: {
    name: string;
  };
  period: RangeOfDateOnly;
};

const restrictionsTableHeaders = computed(() => [
  {
    key: 'number',
    title: '#',
    width: 48,
    sortRaw: (v1: ServiceGroupRestriction, v2: ServiceGroupRestriction) =>
      v2.restriction.number > v1.restriction.number ? -1 : 1,
  },
  {
    key: 'serviceGroup',
    title: 'Группа услуг',
    sortRaw: (v1: ServiceGroupRestriction, v2: ServiceGroupRestriction) =>
      v2.restrictionFactor.name > v1.restrictionFactor.name ? -1 : 1,
  },
  {
    key: 'period',
    title: 'Период',
    sortRaw: (v1: ServiceGroupRestriction, v2: ServiceGroupRestriction) =>
      `${toShortDateFormat(v2.period.from)} ${toShortDateFormat(v2.period.to)}` >
      `${toShortDateFormat(v1.period.from)} ${toShortDateFormat(v1.period.to)}`
        ? -1
        : 1,
  },
  {
    key: 'options',
    title: 'Условие',
    sortable: false,
  },
  {
    key: 'actions',
    sortable: false,
    width: 40,
  },
]);

const restrictionsTableItems = computed(() => {
  return serviceGroupsRestrictions.value?.table.map((item) => ({ ...item, period: item.restriction.period })) ?? [];
});

const filteredRestrictionsTableItems = computed(() => {
  return restrictionsTableItems.value.filter((item) => {
    return (
      (!filters.serviceGroup || item.restrictionFactor.cid === filters.serviceGroup) &&
      (filters.confirmationType === null ||
        item.restriction.options.options?.isNeedConfirm === filters.confirmationType)
    );
  });
});

//#region Фильтры
const filters = reactive({
  serviceGroup: null,
  confirmationType: null,
});

const serviceGroupItems = computed(() => {
  return serviceGroupsRestrictions.value?.grid.map((item) => item.restrictionFactor) ?? [];
});

const confirmationTypeItems = [
  { title: 'Запрос', value: true },
  { title: 'Гарантия', value: false },
];
//#endregion Фильтры

//#region Mobile calendar
const selectedTable = ref<ChessTableType<ServiceGroupsRestrictionsCellDataType>>();
const selectedRow = ref<ChessTableRowType<ServiceGroupsRestrictionsCellDataType>>();

const rowList = computed(
  () =>
    selectedTable.value?.rows.map((item) => ({
      title: item.fixed?.title,
      value: item,
    })) ?? [],
);

const handleChessOpen = () => {
  selectedTable.value = chessItems.value[0].tables[0];
  selectedRow.value = selectedTable.value.rows[0];
  setIsChessCalendarDialogOpen(true);
};
//#endregion Mobile calendar

const handleRestrictionShow = (item: ArrayElementType<typeof restrictionsTableItems.value>) => {
  if (!serviceGroupsRestrictions.value) return;

  if (display.mdAndUp.value) {
    chessRef.value?.scrollTo(item.period.from);
  } else {
    const rowIndex = serviceGroupsRestrictions.value?.grid.findIndex((serviceGroupItem) => {
      return serviceGroupItem.restrictionFactor.cid === item.restrictionFactor.cid;
    });

    selectedTable.value = chessItems.value[0].tables[0];
    selectedRow.value = selectedTable.value.rows[rowIndex];

    setIsChessCalendarDialogOpen(true);
    nextTick(() => {
      chessCalendarRef.value?.scrollTo(item.period.from);
    });
  }
};
const handleRestrictionEdit = (item: ArrayElementType<typeof restrictionsTableItems.value>) => {
  selectedChessCells.value = Object.values(
    getChessCellsFromGridItem(item.period, {
      serviceGroup: item.restrictionFactor.cid,
      restrictionId: item.restriction.restrictionId,
    }),
  );
  setIsRestrictionDialogOpen(true);
};

const handleRestrictionRemove = (item: ArrayElementType<typeof restrictionsTableItems.value>) => {
  selectedChessCells.value = Object.values(
    getChessCellsFromGridItem(item.period, {
      serviceGroup: item.restrictionFactor.cid,
      restrictionId: item.restriction.restrictionId,
    }),
  );
  setIsRemoveRestrictionDialogOpen(true);
};

const handleChessSelect = async (cells: ChessDateCellType<ServiceGroupsRestrictionsCellDataType>[]) => {
  selectedChessCells.value = cells;

  if (cells.length) {
    setIsRestrictionDialogOpen(true);
  }
};

const removeRestrictionAction = async () => {
  if (!selectedChessCells.value[0]) return;

  await removeServiceGroupsRestriction({
    restrictionId: Number(selectedChessCells.value[0].data?.restrictionId),
    tariffScheduleCid: tariffScheduleCid.value,
  });
};

const getChessCellsFromGridItem = (period: RangeOfDateOnly, data: ServiceGroupsRestrictionsCellDataType) => {
  const result: ChessTableRowCellsType<ServiceGroupsRestrictionsCellDataType> = {};
  let options = serviceGroupsRestrictions.value?.defaults.find(
    (item) => item.restrictionFactorKey.serviceGroupCid === data.serviceGroup,
  )?.defaultOptions;

  let rangeId;

  if (data.restrictionId) {
    const gridItemRestriction = serviceGroupsRestrictions.value?.table.find(
      (item) => item.restriction.restrictionId === data.restrictionId,
    );

    if (gridItemRestriction) {
      options = gridItemRestriction.restriction.options;
      rangeId = gridItemRestriction.restriction.number;
    }
  }

  let periodDate = period.from;

  while (periodDate <= period.to) {
    result[periodDate] = {
      date: periodDate,
      content: `<div class="text-ellipsis mb-md-1">${options?.isOpened ? 'Открыта' : 'Закрыта'}</div>
                <div class="text-ellipsis mt-md-05">${options?.isOpened ? (options?.options?.isNeedConfirm ? 'Запрос' : 'Гарантия') : '-'}</div>
                <div class="text-ellipsis mt-md-05">${options?.isOpened ? (options?.options?.orderPeriod ? 'Разрешен' : 'Запрещен') : '-'}</div>
                <div class="text-ellipsis mt-md-05">${options?.isOpened ? options?.options?.orderPeriod?.minOrderPeriod || 'Не ограничен' : '-'}</div>
                <div class="text-ellipsis mt-md-05">${options?.isOpened ? options?.options?.orderPeriod?.maxOrderPeriod || 'Не ограничен' : '-'}</div>`,
      rangeId,
      data,
    };
    periodDate = dayjs(periodDate).add(1, 'day').format('YYYY-MM-DD');
  }

  return result;
};

const { isLoading } = useFetch(async () => {
  await getServiceGroupsRestrictions(tariffScheduleCid.value);
});
</script>
